import { Injectable } from '@angular/core';
import moment, { Moment } from 'moment-timezone';
import { LegFiJwtService } from './auth/legfi-jwt.service';

/**
 * Date/Time Format Constants
 *
 * Standardized formats used throughout the application for consistency.
 */
export const DateTimeFormats = {
    /** API date format: YYYY-MM-DD */
    API_DATE: 'YYYY-MM-DD',
    /** API datetime format with seconds: YYYY-MM-DD HH:mm:ss */
    API_DATETIME: 'YYYY-MM-DD HH:mm:ss',
    /** API datetime format without seconds: YYYY-MM-DD HH:mm */
    API_DATETIME_SHORT: 'YYYY-MM-DD HH:mm',
    /** Display format: Jan 15, 2026 */
    DISPLAY_DATE: 'MMM D, YYYY',
    /** Display format with day: Thursday, January 15, 2026 */
    DISPLAY_DATE_FULL: 'dddd, MMMM D, YYYY',
    /** Display format: 01/15/2026 */
    DISPLAY_DATE_NUMERIC: 'MM/DD/YYYY',
    /** Time format: 2:30 PM */
    DISPLAY_TIME_12H: 'h:mm A',
    /** Time format: 14:30 */
    DISPLAY_TIME_24H: 'HH:mm',
    /** Combined datetime: Jan 15, 2026, 2:30 PM */
    DISPLAY_DATETIME: 'MMM D, YYYY, h:mm A',
} as const;

/**
 * Centralized DateTime Utilities Service
 *
 * Provides standardized date/time parsing, formatting, and timezone handling
 * for consistent date operations across the application.
 *
 * ## Key Features
 * - Consistent timezone handling using organization timezone from JWT
 * - Standardized API date parsing (UTC to org timezone)
 * - Standardized API date formatting (local to API format)
 * - Common display formatting methods
 *
 * ## Usage Patterns
 *
 * ### Parsing dates from API responses:
 * ```typescript
 * // Parse UTC date string from API to org timezone
 * const date = this.dateTimeUtils.parseApiDate('2026-01-15');
 *
 * // Parse UTC datetime string from API to org timezone
 * const datetime = this.dateTimeUtils.parseApiDateTime('2026-01-15 14:30:00');
 *
 * // Parse date and time separately (common in booking APIs)
 * const bookingTime = this.dateTimeUtils.parseApiDateAndTime('2026-01-15', '14:30:00');
 * ```
 *
 * ### Formatting dates for API requests:
 * ```typescript
 * // Format Date object for API
 * const apiDate = this.dateTimeUtils.formatForApi(new Date());  // '2026-01-15'
 *
 * // Format Moment for API
 * const apiDate = this.dateTimeUtils.formatForApi(moment());  // '2026-01-15'
 * ```
 *
 * ### Getting current time in org timezone:
 * ```typescript
 * const now = this.dateTimeUtils.nowInOrgTimezone();
 * const today = this.dateTimeUtils.todayInOrgTimezone();  // Start of day
 * ```
 *
 * @example
 * // In a component or service
 * constructor(private dateTimeUtils: DateTimeUtilsService) {}
 *
 * loadData() {
 *     this.api.getData().subscribe(response => {
 *         // Parse API date to display format
 *         this.displayDate = this.dateTimeUtils.parseApiDate(response.createdAt)
 *             .format(DateTimeFormats.DISPLAY_DATE_FULL);
 *     });
 * }
 */
@Injectable({ providedIn: 'root' })
export class DateTimeUtilsService {

    // ==========================================
    // TIMEZONE HELPERS
    // ==========================================

    /**
     * Get the organization's timezone from JWT.
     * Falls back to browser timezone if JWT is not available.
     *
     * @returns Timezone string (e.g., 'America/Chicago')
     */
    getOrgTimezone(): string {
        return LegFiJwtService.getTimezone() || moment.tz.guess();
    }

    /**
     * Get the current moment in the organization's timezone.
     *
     * @returns Current moment in org timezone
     */
    nowInOrgTimezone(): Moment {
        return moment.tz(this.getOrgTimezone());
    }

    /**
     * Get today's date at midnight (start of day) in the organization's timezone.
     * Useful for date comparisons and calendar min dates.
     *
     * @returns Moment representing start of today in org timezone
     */
    todayInOrgTimezone(): Moment {
        return moment.tz(this.getOrgTimezone()).startOf('day');
    }

    /**
     * Get today's date at midnight as a JavaScript Date object.
     * Useful for Angular Material datepickers.
     *
     * @returns Date object representing start of today in org timezone
     */
    todayAsDate(): Date {
        return this.parseLocalDate(this.todayInOrgTimezone().format(DateTimeFormats.API_DATE))!;
    }

    // ==========================================
    // API DATE PARSING (UTC to Org Timezone)
    // ==========================================

    /**
     * Parse a UTC date string from API response to org timezone.
     * This is the standard method for parsing dates received from the backend.
     *
     * @param dateStr - Date string from API (e.g., '2026-01-15' or '2026-01-15 14:30:00')
     * @param format - Optional format string (defaults to auto-detection)
     * @returns Moment in org timezone, or null if dateStr is falsy
     *
     * @example
     * // Parse date-only
     * const date = dateTimeUtils.parseApiDate('2026-01-15');
     *
     * // Parse datetime
     * const datetime = dateTimeUtils.parseApiDate('2026-01-15 14:30:00');
     */
    parseApiDate(dateStr: string | null | undefined, format?: string): Moment | null {
        if (!dateStr) {
            return null;
        }

        const timezone = this.getOrgTimezone();

        // Auto-detect format if not provided
        if (!format) {
            format = dateStr.includes(' ')
                ? DateTimeFormats.API_DATETIME
                : DateTimeFormats.API_DATE;
        }

        return moment.utc(dateStr, format).tz(timezone);
    }

    /**
     * Parse a UTC datetime string from API response to org timezone.
     * Alias for parseApiDate with datetime format.
     *
     * @param datetimeStr - Datetime string from API (e.g., '2026-01-15 14:30:00')
     * @returns Moment in org timezone, or null if datetimeStr is falsy
     */
    parseApiDateTime(datetimeStr: string | null | undefined): Moment | null {
        return this.parseApiDate(datetimeStr, DateTimeFormats.API_DATETIME);
    }

    /**
     * Parse separate date and time strings from API into a single Moment.
     * Common pattern for booking/scheduling APIs where date and time are separate fields.
     *
     * @param dateStr - Date string (e.g., '2026-01-15')
     * @param timeStr - Time string (e.g., '14:30:00' or '14:30')
     * @returns Moment in org timezone, or null if dateStr is falsy
     *
     * @example
     * const bookingStart = dateTimeUtils.parseApiDateAndTime(
     *     booking.startDate,  // '2026-01-15'
     *     booking.startTime   // '14:30:00'
     * );
     */
    parseApiDateAndTime(dateStr: string | null | undefined, timeStr: string | null | undefined): Moment | null {
        if (!dateStr) {
            return null;
        }

        const timezone = this.getOrgTimezone();
        const time = timeStr || '00:00:00';

        // Detect time format (with or without seconds)
        const timeFormat = time.split(':').length === 3 ? 'HH:mm:ss' : 'HH:mm';
        const fullFormat = `${DateTimeFormats.API_DATE} ${timeFormat}`;

        return moment.tz(`${dateStr} ${time}`, fullFormat, timezone);
    }

    /**
     * Parse a date string as a local date (no timezone conversion).
     * Used for calendar components that operate in browser local time.
     *
     * @param dateStr - Date string in YYYY-MM-DD format
     * @returns Date object at midnight in browser's local timezone
     *
     * @example
     * // For calendar display where we want the literal date without timezone shift
     * const calendarDate = dateTimeUtils.parseLocalDate('2026-01-15');
     */
    parseLocalDate(dateStr: string | null | undefined): Date | null {
        if (!dateStr) {
            return null;
        }

        const [year, month, day] = dateStr.split('-').map(Number);
        return new Date(year, month - 1, day);
    }

    // ==========================================
    // API DATE FORMATTING (Local to API Format)
    // ==========================================

    /**
     * Format a date for API requests (YYYY-MM-DD).
     * Handles Date objects, Moment objects, and date strings.
     *
     * @param date - Date to format (Date, Moment, or string)
     * @returns Formatted date string for API, or empty string if date is falsy
     *
     * @example
     * const apiDate = dateTimeUtils.formatForApi(new Date());  // '2026-01-15'
     * const apiDate = dateTimeUtils.formatForApi(moment());    // '2026-01-15'
     */
    formatForApi(date: Date | Moment | string | null | undefined): string {
        if (!date) {
            return '';
        }

        if (typeof date === 'string') {
            return date; // Assume already formatted
        }

        if (moment.isMoment(date)) {
            return date.format(DateTimeFormats.API_DATE);
        }

        // Date object - format without timezone conversion
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    /**
     * Format a datetime for API requests (YYYY-MM-DD HH:mm:ss).
     *
     * @param datetime - Datetime to format (Date or Moment)
     * @returns Formatted datetime string for API
     */
    formatDateTimeForApi(datetime: Date | Moment | null | undefined): string {
        if (!datetime) {
            return '';
        }

        if (moment.isMoment(datetime)) {
            return datetime.format(DateTimeFormats.API_DATETIME);
        }

        return moment(datetime).format(DateTimeFormats.API_DATETIME);
    }

    /**
     * Convert a Moment to ISO string for API (preserves timezone info).
     *
     * @param date - Moment to convert
     * @returns ISO 8601 string
     */
    toISOString(date: Moment | null | undefined): string {
        if (!date) {
            return '';
        }
        return date.toISOString();
    }

    // ==========================================
    // DISPLAY FORMATTING
    // ==========================================

    /**
     * Format a date for display using a predefined or custom format.
     *
     * @param date - Date to format (string, Date, or Moment)
     * @param format - Format string (use DateTimeFormats constants)
     * @returns Formatted string, or empty string if date is falsy
     *
     * @example
     * const display = dateTimeUtils.formatDisplay(apiDate, DateTimeFormats.DISPLAY_DATE_FULL);
     * // 'Thursday, January 15, 2026'
     */
    formatDisplay(date: string | Date | Moment | null | undefined, format: string): string {
        if (!date) {
            return '';
        }

        let m: Moment;
        if (typeof date === 'string') {
            m = this.parseApiDate(date) || moment.invalid();
        } else if (moment.isMoment(date)) {
            m = date;
        } else {
            // Date objects from calendar components are created at midnight in browser-local
            // timezone (new Date(y,m,d)). moment(date) converts to the global default timezone
            // (org TZ via moment.tz.setDefault in refresh-token interceptor), which shifts the
            // day backward when the user's browser TZ is ahead of the org TZ.
            // Extract date components via formatForApi (uses getFullYear/getMonth/getDate)
            // and create the moment directly in org TZ to avoid the shift.
            const dateStr = this.formatForApi(date);
            m = moment.tz(dateStr, DateTimeFormats.API_DATE, this.getOrgTimezone());
        }

        return m.isValid() ? m.format(format) : '';
    }

    /**
     * Format a date for display in the user's locale format.
     *
     * @param date - Date to format
     * @returns Localized date string (e.g., 'Jan 15, 2026')
     */
    formatDateDisplay(date: string | Date | Moment | null | undefined): string {
        return this.formatDisplay(date, DateTimeFormats.DISPLAY_DATE);
    }

    /**
     * Format a date with full day name for display.
     *
     * @param date - Date to format
     * @returns Full date string (e.g., 'Thursday, January 15, 2026')
     */
    formatDateDisplayFull(date: string | Date | Moment | null | undefined): string {
        return this.formatDisplay(date, DateTimeFormats.DISPLAY_DATE_FULL);
    }

    /**
     * Format a numeric date for display.
     *
     * @param date - Date to format
     * @returns Numeric date string (e.g., '01/15/2025')
     */
    formatDateDisplayNumeric(date: string | Date | Moment | null | undefined): string {
        return this.formatDisplay(date, DateTimeFormats.DISPLAY_DATE_NUMERIC);
    }

    // ==========================================
    // DATE COMPARISON HELPERS
    // ==========================================

    /**
     * Check if a date/moment is before the current time in org timezone.
     *
     * @param date - Date to check
     * @returns True if date is in the past
     */
    isInPast(date: Moment | null | undefined): boolean {
        if (!date || !date.isValid()) {
            return false;
        }
        return date.isBefore(this.nowInOrgTimezone());
    }

    /**
     * Check if a date/moment is after the current time in org timezone.
     *
     * @param date - Date to check
     * @returns True if date is in the future
     */
    isInFuture(date: Moment | null | undefined): boolean {
        if (!date || !date.isValid()) {
            return false;
        }
        return date.isAfter(this.nowInOrgTimezone());
    }

    /**
     * Check if a date is today in the organization's timezone.
     *
     * @param date - Date to check (string, Date, or Moment)
     * @returns True if date is today
     */
    isToday(date: string | Date | Moment | null | undefined): boolean {
        if (!date) {
            return false;
        }

        const today = this.todayInOrgTimezone();

        let checkDate: Moment;
        if (typeof date === 'string') {
            // Date-only strings (YYYY-MM-DD) represent org-local dates, not UTC.
            // Parse in org timezone to avoid day-shift in western timezones.
            if (!date.includes(' ')) {
                checkDate = moment.tz(date, DateTimeFormats.API_DATE, this.getOrgTimezone());
            } else {
                checkDate = this.parseApiDate(date) || moment.invalid();
            }
        } else if (moment.isMoment(date)) {
            checkDate = date;
        } else {
            // Same fix as formatDisplay: avoid moment(date) timezone shift from setDefault.
            const dateStr = this.formatForApi(date);
            checkDate = moment.tz(dateStr, DateTimeFormats.API_DATE, this.getOrgTimezone());
        }

        return checkDate.isValid() && checkDate.isSame(today, 'day');
    }

    // ==========================================
    // RELATIVE DATE HELPERS
    // ==========================================

    /**
     * Create a moment that is a certain number of days from now in org timezone.
     *
     * @param days - Number of days to add (positive) or subtract (negative)
     * @returns Moment representing the date
     *
     * @example
     * const dueDate = dateTimeUtils.daysFromNow(7);  // 7 days from now
     * const yesterday = dateTimeUtils.daysFromNow(-1);  // Yesterday
     */
    daysFromNow(days: number): Moment {
        return this.nowInOrgTimezone().add(days, 'days');
    }

    /**
     * Create a moment representing a future date at start of day in org timezone.
     * Useful for setting default due dates.
     *
     * @param days - Number of days from today
     * @returns Moment at start of the target day
     */
    futureDateStartOfDay(days: number): Moment {
        return this.todayInOrgTimezone().add(days, 'days');
    }

    /**
     * Add business days to a date, skipping weekends (Saturday and Sunday).
     *
     * @param startDate - Starting date
     * @param numDays - Number of business days to add
     * @returns New Moment advanced by the specified number of business days
     */
    addBusinessDays(startDate: Moment, numDays: number): Moment {
        const SUNDAY = 0;
        const SATURDAY = 6;
        let remaining = numDays;
        const date = startDate.clone();

        while (remaining > 0) {
            date.add(1, 'days');
            if (date.day() !== SUNDAY && date.day() !== SATURDAY) {
                remaining--;
            }
        }

        return date;
    }
}
